import { defineStore, acceptHMRUpdate } from 'pinia';
import { useArticlesStore } from './articlesStore';
import type { ArticleModel, WordPressArticleDTO } from 'data-types-config';
import { logger } from '../utils/logger';

export enum SearchType {
  All = 'all',
  Articles = 'articles',
  Videos = 'videos', // ← antes era 'questions'
  Books = 'books',
  Beliefs = 'beliefs',
}

export const SearchTypeLabels: Record<SearchType, string> = {
  [SearchType.All]: 'All',
  [SearchType.Videos]: 'Videos',
  [SearchType.Articles]: 'Articles',
  [SearchType.Books]: 'Books',
  [SearchType.Beliefs]: 'Beliefs',
};

export function labelToSearchType(label: string): SearchType {
  const entry = Object.entries(SearchTypeLabels).find(
    ([, value]) => value === label,
  );
  return (entry?.[0] as SearchType) ?? SearchType.All;
}

export function searchTypeToLabel(type: SearchType): string {
  return SearchTypeLabels[type] ?? 'All';
}

export interface SearchResult {
  _index: string;
  [key: string]: any;
}

interface SearchPayload {
  q: string;
  type: SearchType;
}

// 🔐 internal index mapping
// Articles + Videos ahora vienen del mismo índice EP
const indexMap: Record<Exclude<SearchType, SearchType.All>, string> = {
  [SearchType.Articles]: 'cptrueislamcouk-post-1',
  [SearchType.Videos]: 'cptrueislamcouk-post-1',
  [SearchType.Books]: 'books',
  [SearchType.Beliefs]: 'beliefs',
};

export const useSearchStore = defineStore('SearchStore', {
  state: () => ({
    query: '' as string,
    type: SearchType.All,
    lastType: SearchType.All,
    selectedTabLabel: 'All',
    results: [] as SearchResult[],
    isLoading: false,
    error: null as string | null,
    verbose: false,
  }),

  getters: {
    resultsList: (state) => state.results,
    loadingStatus: (state) => state.isLoading,
    currentError: (state) => state.error,
    currentQuery: (state) => state.query,
    currentType: (state) => state.type,
  },

  actions: {
    async search(q: string, type: SearchType = SearchType.All) {
      const trimmed = q.trim();
      if (!this.shouldSearch(trimmed, type)) return;

      this.prepareSearch(trimmed, type);

      try {
        const data = await this.fetchResults({ q: trimmed, type });
        this.setResults(data);
      } catch (err) {
        this.setError(err);
      } finally {
        this.setLoadingState(false);
      }
    },

    async maybeSearch(q: string, type: SearchType) {
      const trimmed = q.trim();
      if (!trimmed) {
        this.clearResults();
        return;
      }

      const hasResults = this.resultsList.length > 0;

      let hasTypeInStore = false;

      if (type === SearchType.All) {
        hasTypeInStore = hasResults;
      } else if (type === SearchType.Articles) {
        // Articles = post_type articles + page
        hasTypeInStore = this.resultsList.some(
          (r) => r.post_type === 'articles' || r.post_type === 'page',
        );
      } else if (type === SearchType.Videos) {
        // Videos = post_type videos
        hasTypeInStore = this.resultsList.some((r) => r.post_type === 'videos');
      } else {
        // Legacy: books / beliefs siguen basados en _index
        const index = this.getIndexForType(type);
        hasTypeInStore = index
          ? this.resultsList.some((r) => r._index === index)
          : false;
      }

      if (!hasTypeInStore) {
        await this.search(trimmed, type);
      }
    },

    shouldSearch(q: string, type: SearchType): boolean {
      const trimmed = q.trim();
      return !!trimmed && !(this.query === trimmed && this.lastType === type);
    },

    prepareSearch(q: string, type: SearchType) {
      this.query = q;
      this.type = type;
      this.lastType = type;
      this.results = [];
      this.error = null;
      this.setLoadingState(true);
    },

    async fetchResults(payload: SearchPayload): Promise<SearchResult[]> {
      const res = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Status ${res.status}`);
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error('Unexpected response format');
      }

      return data;
    },

    getIndexForType(type: SearchType): string | undefined {
      return indexMap[type as Exclude<SearchType, SearchType.All>];
    },

    isArticle(item: SearchResult): boolean {
      const type = item.postType || item.post_type;
      return type === 'articles' || type === 'page';
    },

    isQuestion(item: SearchResult): boolean {
      const type = item.postType || item.post_type;
      return type === 'videos';
    },

    setResults(data: SearchResult[]) {
      this.results = JSON.parse(JSON.stringify(data));

      // Unify data flow: Seed the ArticlesStore with any articles found in search
      const articlesStore = useArticlesStore();
      const articlesFound = this.results.filter((item) => this.isArticle(item));

      if (articlesFound.length > 0) {
        articlesStore.addArticles(articlesFound as unknown as ArticleModel[]);
      }
    },

    setError(error: unknown) {
      const message = error instanceof Error ? error.message : 'Search failed.';
      this.error = message;
      if (this.verbose) logger.error('Search error:', error);
    },

    setLoadingState(value: boolean) {
      this.isLoading = value;
    },

    updateSelectedTabLabel(label: string) {
      this.selectedTabLabel = label;
      this.type = labelToSearchType(label);
    },

    clearResults() {
      this.query = '';
      this.results = [];
      this.error = null;
      this.type = SearchType.All;
      this.lastType = SearchType.All;
      this.selectedTabLabel = 'All';
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot));
}
